import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Popover,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getColors, shadows, getServiceIcon } from "./services.styles";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";
import SpaIcon from "@mui/icons-material/Spa";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import LaptopIcon from "@mui/icons-material/Laptop";
import DevicesIcon from "@mui/icons-material/Devices";
import ServiceFilterDropdown from "./ServiceFilterDropdown";

const getServiceIconComponent = (serviceName) => {
  const name = serviceName?.toLowerCase() || "";
  if (name.includes("skin")) return <SpaIcon sx={{ fontSize: 18 }} />;
  if (name.includes("dental"))
    return <MedicalServicesIcon sx={{ fontSize: 18 }} />;
  if (name.includes("hair")) return <ContentCutIcon sx={{ fontSize: 18 }} />;
  if (name.includes("web") || name.includes("development"))
    return <LaptopIcon sx={{ fontSize: 18 }} />;
  if (name.includes("electronic") || name.includes("gl"))
    return <DevicesIcon sx={{ fontSize: 18 }} />;
  return <SpaIcon sx={{ fontSize: 18 }} />;
};

const MetricBadge = ({ value, type, suffix = "" }) => {
  const theme = useTheme();
  const colors = getColors(theme);

  const getColor = () => {
    if (type === "conversion") {
      if (value >= 15) return { bg: "#D1FAE5", text: "#059669" };
      if (value >= 10) return { bg: "#E0E7FF", text: "#4F46E5" };
      if (value >= 5) return { bg: "#FEF3C7", text: "#D97706" };
      return { bg: "#FEE2E2", text: "#DC2626" };
    }
    if (type === "dropoff") {
      if (value === 0) return { bg: "#D1FAE5", text: "#059669" };
      if (value <= 5) return { bg: "#E0E7FF", text: "#4F46E5" };
      if (value <= 10) return { bg: "#FEF3C7", text: "#D97706" };
      return { bg: "#FEE2E2", text: "#DC2626" };
    }
    return { bg: colors.bgPrimary, text: colors.textSecondary };
  };

  const style = getColor();

  return (
    <Chip
      label={`${value}${suffix}`}
      size="small"
      sx={{
        height: 24,
        fontSize: 12,
        fontWeight: 700,
        bgcolor: style.bg,
        color: style.text,
        borderRadius: 1,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const DetailedServicePerformance = ({
  data = [],
  allServices = [],
  onFilterChange,
}) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const filteredData = data.filter((row) =>
    row.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleViewDetails = (row) => {
    // Navigate to the detailed service performance page
    const encodedServiceName = encodeURIComponent(row.name || row.id || "");
    navigate(`/reports/detailed-service-performance/${encodedServiceName}`);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = (filters) => {
    onFilterChange?.(filters);
    handleFilterClose();
  };

  const formatRevenue = (value) => {
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return `₹${value.toLocaleString()}`;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        overflow: "hidden",
        mt: 1.5,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${colors.borderLight}`,
          bgcolor: colors.bgPrimary,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: colors.textMuted,
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              Detailed Service Performance
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: colors.textSecondary,
              }}
            >
              {data.length} services • {filteredData.length} shown
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              placeholder="Search services..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 16, color: colors.textMuted }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                  bgcolor: "#FFFFFF",
                  height: 38,
                  "& fieldset": {
                    borderColor: colors.borderLight,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.border,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary,
                    borderWidth: 1,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                  px: 1,
                },
              }}
            />
            <IconButton
              onClick={handleFilterClick}
              size="small"
              sx={{
                color: colors.textMuted,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 2,
                width: 38,
                height: 38,
                bgcolor: "#FFFFFF",
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                  borderColor: colors.border,
                },
              }}
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Table Section */}
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{ borderCollapse: "separate", borderSpacing: "0" }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  whiteSpace: "nowrap",
                  borderTop: "none",
                }}
              >
                Service Name
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  textAlign: "center",
                  borderTop: "none",
                }}
              >
                Total Leads
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  textAlign: "center",
                  borderTop: "none",
                }}
              >
                Conv. Rate
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  textAlign: "center",
                  borderTop: "none",
                }}
              >
                Revenue
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  textAlign: "center",
                  borderTop: "none",
                }}
              >
                Drop-off Rate
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${colors.borderLight}`,
                  py: 2,
                  px: 2.5,
                  bgcolor: colors.bgPrimary,
                  textAlign: "center",
                  borderTop: "none",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    py: 12,
                    textAlign: "center",
                    borderBottom: "none",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: 48,
                        fontWeight: 700,
                        color: colors.borderLight,
                        mb: 2,
                      }}
                    >
                      {searchQuery ? "🔍" : "📊"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: colors.textPrimary,
                        mb: 1,
                      }}
                    >
                      {searchQuery ? "No services found" : "No data available"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: colors.textSecondary,
                      }}
                    >
                      {searchQuery
                        ? `No services matching "${searchQuery}"`
                        : "Try adjusting your date filter"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row, index) => {
                const iconStyle = getServiceIcon(row.name);
                const isPositiveTrend = row.convTrend > 0;
                const isNegativeTrend = row.convTrend < 0;

                return (
                  <TableRow
                    key={row.id || row.name}
                    sx={{
                      borderBottom: `1px solid ${colors.borderLight}`,
                      "&:hover": {
                        bgcolor: colors.bgPrimary,
                        "& td": {
                          bgcolor: colors.bgPrimary,
                        },
                      },
                      "&:last-child td": {
                        borderBottom: "none",
                      },
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={() => handleViewDetails(row)}
                  >
                    <TableCell sx={{ py: 2.5, px: 2.5 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: iconStyle.bg,
                            color: iconStyle.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          }}
                        >
                          {getServiceIconComponent(row.name)}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: colors.textPrimary,
                              lineHeight: 1.3,
                            }}
                          >
                            {row.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.textMuted,
                              mt: 0.25,
                            }}
                          >
                            {row.id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ py: 2.5, px: 2.5, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: colors.textPrimary,
                        }}
                      >
                        {row.totalLeads?.toLocaleString() ||
                          row.totalBookings?.toLocaleString() ||
                          0}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2.5, px: 2.5, textAlign: "center" }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                      >
                        <MetricBadge
                          value={row.convRate}
                          type="conversion"
                          suffix="%"
                        />
                        {row.convTrend !== 0 && (
                          <Box
                            sx={{
                              p: 0.25,
                              borderRadius: 1,
                              bgcolor: isPositiveTrend
                                ? "#D1FAE5"
                                : isNegativeTrend
                                  ? "#FEE2E2"
                                  : colors.bgPrimary,
                            }}
                          >
                            {isPositiveTrend ? (
                              <TrendingUpIcon
                                sx={{ fontSize: 14, color: "#059669" }}
                              />
                            ) : isNegativeTrend ? (
                              <TrendingDownIcon
                                sx={{ fontSize: 14, color: "#DC2626" }}
                              />
                            ) : (
                              <RemoveIcon
                                sx={{ fontSize: 14, color: colors.textMuted }}
                              />
                            )}
                          </Box>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ py: 2.5, px: 2.5, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: colors.success,
                        }}
                      >
                        {formatRevenue(row.revenue)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2.5, px: 2.5, textAlign: "center" }}>
                      <MetricBadge
                        value={row.dropOffRate}
                        type="dropoff"
                        suffix="%"
                      />
                    </TableCell>

                    <TableCell sx={{ py: 2.5, px: 2.5, textAlign: "center" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(row);
                        }}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: colors.primary,
                          borderColor: colors.borderLight,
                          textTransform: "none",
                          py: 0.75,
                          px: 2,
                          minWidth: 80,
                          borderRadius: 1.5,
                          bgcolor: "#FFFFFF",
                          "&:hover": {
                            borderColor: colors.primary,
                            bgcolor: colors.primaryAlpha("8"),
                          },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: shadows.dropdown,
              border: `1px solid ${colors.borderLight}`,
            },
          },
        }}
      >
        <ServiceFilterDropdown
          onApply={handleApplyFilters}
          onClose={handleFilterClose}
          allServices={allServices || []}
        />
      </Popover>
    </Box>
  );
};

export default DetailedServicePerformance;
